/**
 * FREEWEEK-1 / OPEN-1 — the Founding Pass, offered inside the app.
 *
 * Rennan (Oct 5, kept Oct 6): founders start like everyone else — now
 * with the 7-day trial — and the Pass is offered on Today and in
 * Billing until it closes, instead of being the first thing their email
 * asks for. `founding_offer_for_me()` answers
 * for the signed-in account: is it a founding member (by its email, or
 * the founding link it signed up from), has it bought a Pass, and is
 * the Pass still open. The token is the member's own — it opens their
 * founding page, where the Pass is bought.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { FOUNDING_PASS } from '@/config/founding';

export interface FoundingOffer {
  token: string;
  hasPass: boolean;
  closesAt: Date;
}

export function useFoundingOffer(): { offer: FoundingOffer | null; isLoading: boolean } {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['founding-offer', user?.id],
    queryFn: async (): Promise<FoundingOffer | null> => {
      const { data: raw, error } = await (supabase as any).rpc('founding_offer_for_me');
      if (error || !raw) return null;
      const d = raw as { token?: string; has_pass?: boolean; closes_at?: string | null; open?: boolean };
      if (!d.token || d.open === false) return null;
      const closesAt = new Date(d.closes_at ?? FOUNDING_PASS.closesAt);
      if (Number.isNaN(closesAt.getTime())) return null;
      return { token: d.token, hasPass: !!d.has_pass, closesAt };
    },
    enabled: !!user && FOUNDING_PASS.onSale,
    staleTime: 5 * 60_000,
  });
  const offer = data && !data.hasPass && data.closesAt.getTime() > Date.now() ? data : null;
  return { offer, isLoading: isLoading && !!user };
}
